/**
 * System Monitoring component for multi-tenant admin system monitoring and analytics
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Monitor as MonitorIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  NetworkCheck as NetworkIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useAdmin } from '../../contexts/AdminContext';
import { SystemMetric, SecurityEvent } from '../../types/admin';
import { formatDate, formatNumber } from '../../utils/formatters';

interface SystemMonitoringProps {
  onNavigate?: (path: string) => void;
}

const SystemMonitoring: React.FC<SystemMonitoringProps> = ({ onNavigate }) => {
  const {
    systemMetrics,
    securityEvents,
    securityEventsLoading,
    securityEventsError,
    loadSystemMetrics,
    loadSecurityEvents,
    clearError
  } = useAdmin();

  const [selectedMetric, setSelectedMetric] = useState<string>('');
  const [timeRange, setTimeRange] = useState<number>(24);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<SecurityEvent | null>(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);

  useEffect(() => {
    loadSystemMetrics(selectedMetric, timeRange);
    loadSecurityEvents({ page: 1, page_size: 50 });
  }, [loadSystemMetrics, loadSecurityEvents, selectedMetric, timeRange]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        loadSystemMetrics(selectedMetric, timeRange),
        loadSecurityEvents({ page: 1, page_size: 50 })
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <ErrorIcon color="error" />;
      case 'high':
        return <WarningIcon color="warning" />;
      case 'medium':
        return <InfoIcon color="info" />;
      case 'low':
        return <CheckCircleIcon color="success" />;
      default:
        return <InfoIcon color="disabled" />;
    }
  };

  const getMetricIcon = (metricName: string) => {
    if (metricName.includes('cpu')) return <MonitorIcon />;
    if (metricName.includes('memory')) return <MemoryIcon />;
    if (metricName.includes('disk')) return <StorageIcon />;
    if (metricName.includes('network')) return <NetworkIcon />;
    return <InfoIcon />;
  };

  const getMetricValue = (metric: SystemMetric) => {
    const value = parseFloat(metric.metric_value);
    if (metric.metric_unit === 'percentage') {
      return `${value.toFixed(1)}%`;
    }
    if (metric.metric_unit === 'bytes') {
      return formatBytes(value);
    }
    if (metric.metric_unit === 'count') {
      return formatNumber(value);
    }
    return metric.metric_value;
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getMetricStatus = (metric: SystemMetric) => {
    const value = parseFloat(metric.metric_value);
    if (metric.metric_name.includes('cpu') || metric.metric_name.includes('memory')) {
      if (value > 90) return { status: 'critical', color: 'error' };
      if (value > 75) return { status: 'warning', color: 'warning' };
      return { status: 'normal', color: 'success' };
    }
    return { status: 'normal', color: 'success' };
  };

  const handleEventClick = (event: SecurityEvent) => {
    setSelectedEvent(event);
    setEventDialogOpen(true);
  };

  const handleEventDialogClose = () => {
    setEventDialogOpen(false);
    setSelectedEvent(null);
  };

  if (securityEventsLoading && !securityEvents.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (securityEventsError) {
    return (
      <Alert 
        severity="error" 
        action={
          <Button color="inherit" size="small" onClick={clearError}>
            Dismiss
          </Button>
        }
      >
        {securityEventsError}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" display="flex" alignItems="center" gap={1}>
          <MonitorIcon />
          System Monitoring
        </Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh Data">
            <IconButton 
              onClick={handleRefresh} 
              disabled={refreshing}
              color="primary"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Metric Type</InputLabel>
              <Select
                value={selectedMetric}
                label="Metric Type"
                onChange={(e) => setSelectedMetric(e.target.value)}
              >
                <MenuItem value="">All Metrics</MenuItem>
                <MenuItem value="cpu_usage">CPU Usage</MenuItem>
                <MenuItem value="memory_usage">Memory Usage</MenuItem>
                <MenuItem value="disk_usage">Disk Usage</MenuItem>
                <MenuItem value="network_usage">Network Usage</MenuItem>
                <MenuItem value="alert_processing_rate">Alert Processing Rate</MenuItem>
                <MenuItem value="webhook_execution_rate">Webhook Execution Rate</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={(e) => setTimeRange(Number(e.target.value))}
              >
                <MenuItem value={1}>Last Hour</MenuItem>
                <MenuItem value={6}>Last 6 Hours</MenuItem>
                <MenuItem value={24}>Last 24 Hours</MenuItem>
                <MenuItem value={168}>Last Week</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* System Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {(systemMetrics?.metrics?.length ?? 0) === 0 ? (
          <Grid item xs={12}>
            <Alert severity="info">
              No historical metrics recorded yet. Live host metrics will appear after the next refresh.
            </Alert>
          </Grid>
        ) : (
          systemMetrics?.metrics.map((metric) => {
          const status = getMetricStatus(metric);
          return (
            <Grid item xs={12} sm={6} md={4} key={metric.id}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    {getMetricIcon(metric.metric_name)}
                    <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                      {metric.metric_name.replace(/_/g, ' ')}
                    </Typography>
                    <Chip
                      label={status.status}
                      color={status.color as any}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="h4" mb={1}>
                    {getMetricValue(metric)}
                  </Typography>
                  {metric.metric_name.includes('cpu') || metric.metric_name.includes('memory') ? (
                    <LinearProgress
                      variant="determinate"
                      value={parseFloat(metric.metric_value)}
                      color={status.color as any}
                      sx={{ mb: 1 }}
                    />
                  ) : null}
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(metric.timestamp)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })
        )}
      </Grid>

      {/* Security Events */}
      <Card>
        <CardContent>
          <Typography variant="h6" mb={2} display="flex" alignItems="center" gap={1}>
            <SecurityIcon />
            Security Events
          </Typography>
          
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Severity</TableCell>
                  <TableCell>Event Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {securityEvents.map((event) => (
                  <TableRow key={event.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getSeverityIcon(event.severity)}
                        <Chip
                          label={event.severity.toUpperCase()}
                          color={getSeverityColor(event.severity) as any}
                          size="small"
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {event.event_type.replace(/_/g, ' ')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {event.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {event.ip_address || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={event.resolved ? 'Resolved' : 'Open'}
                        color={event.resolved ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(event.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => handleEventClick(event)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Event Details Dialog */}
      <Dialog open={eventDialogOpen} onClose={handleEventDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Security Event Details
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Event Type
                  </Typography>
                  <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                    {selectedEvent.event_type.replace(/_/g, ' ')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Severity
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    {getSeverityIcon(selectedEvent.severity)}
                    <Chip
                      label={selectedEvent.severity.toUpperCase()}
                      color={getSeverityColor(selectedEvent.severity) as any}
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {selectedEvent.description}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    IP Address
                  </Typography>
                  <Typography variant="body1">
                    {selectedEvent.ip_address || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    User Agent
                  </Typography>
                  <Typography variant="body1" noWrap>
                    {selectedEvent.user_agent || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedEvent.resolved ? 'Resolved' : 'Open'}
                    color={selectedEvent.resolved ? 'success' : 'warning'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedEvent.created_at)}
                  </Typography>
                </Grid>
                {selectedEvent.details && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Additional Details
                    </Typography>
                    <Typography variant="body1" component="pre" sx={{ 
                      backgroundColor: 'grey.100', 
                      p: 1, 
                      borderRadius: 1,
                      fontSize: '0.875rem',
                      overflow: 'auto'
                    }}>
                      {JSON.stringify(selectedEvent.details, null, 2)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEventDialogClose}>Close</Button>
          {selectedEvent && !selectedEvent.resolved && (
            <Button variant="contained" color="primary">
              Mark as Resolved
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SystemMonitoring;
